"use client";

import posthog from "posthog-js";
import { AttributedLink } from "@/components/marketing/attributed-link";
import { getGuideCtaCopy, guideSignupHref } from "@/lib/guides/cta-copy";
import {
  BUSINESS_WALKTHROUGH_CTA,
  BUSINESS_WALKTHROUGH_HREF,
} from "@/lib/marketing/business-walkthrough-cta";

/**
 * Where on a guide page the ask sits — reported as `cta_position`.
 * `post-body` covers the late-page feature boxes ("How The Site Book helps"
 * and kin) that sit after the article body but before the footer ask.
 */
export type GuideCtaPosition =
  | "intro"
  | "mid"
  | "end"
  | "post-tool"
  | "post-example"
  | "post-body";

/**
 * The in-content ask on a guide page.
 *
 * Guides are ~60% of organic landing traffic and, measured on 2026-08-24,
 * produced 2 signups and 0 customers in 90 days. The cause was structural: on
 * DESKTOP the only account link sits at ~96% page depth, below every internal
 * exit, and the one in-content form was <GuideLeadCapture> — an email-for-a-PDF
 * box that persisted no lead and had captured 3 addresses in the product's
 * lifetime. (On mobile a permanent sign-up bar already exists via
 * <StickyMobileCta>, mounted in app/(marketing)/layout.tsx and hidden on md+;
 * it has not moved the number either, which is why an in-content ask inside the
 * reading flow is worth having on both.)
 *
 * PLACEMENT is the whole design, and it is deliberately ABOVE the table of
 * contents, not below it:
 *
 *   - <TableOfContents> is `sticky top-16 z-10` with an opaque `bg-gray-50`
 *     (components/marketing/table-of-contents.tsx:22). Anything placed after it
 *     scrolls UNDERNEATH it within about one card-height.
 *   - A TOC is a routing device. A reader who scans it and jumps to a section
 *     skips everything between the TOC and that heading — which is exactly the
 *     high-intent reader this ask is for.
 *
 * Above the TOC it is occlusion-proof and cannot be routed around.
 *
 * TOPIC COPY (2026-09-30): the headline now comes from the per-guide registry
 * (lib/guides/cta-copy.ts) so the ask names the document the reader is reading
 * about. <GuideEndCta> repeats the same card at the end of the article body —
 * after the last section, before the related-guides directory — so a reader
 * who finishes the guide meets the same tracked ask. Copy rules for both live
 * in the registry header; nothing here may out-promise them.
 *
 * DESTINATION (2026-10-31 conversion programme) is the value-first
 * /start-project job-pack funnel — the same one /features/* and the blog use —
 * built by guideSignupHref(): `utm_content=guide_cta&utm_term=<slug>`, carried
 * on the click event's `destination`. AttributedLink never overwrites a utm
 * key the href already sets, so ad params only fill gaps. /start-project asks
 * about the job first and creates the free account when the reader saves the
 * pack, which is what the copy promises (a free account whose Starter tier
 * covers one real job). It previously went to the bare /signup form: seven
 * fields before any project exists.
 *
 * MID-ARTICLE placement (position "mid"): every guide page renders this card a
 * second time after its middle article section (the ceil(n/2)-th section with
 * a TOC anchor id), pinned by lib/guides/__tests__/cta-copy.test.ts. Long
 * guides otherwise had nothing between the intro card and the footer.
 *
 * EVENT NAME is deliberately NOT `landing_cta_clicked`. That name is owned by
 * <FunnelCtaLink>, which attaches `$feature/funnel-v2` to every emission
 * (components/funnel/funnel-cta-link.tsx:53-60). Emitting it from here — with no
 * variant, from 56 pages that are not in the experiment — would contaminate the
 * funnel-v2 exposure/conversion split with unassigned traffic. `guide_cta_clicked`
 * keeps the two answerable separately.
 *
 * Event payload: the legacy `cta_location` / `destination` / `entry_path`
 * properties are kept unchanged (the 2026-08 baseline was measured on them);
 * `guide_slug` / `cta_position` / `cta_copy` were added 2026-09-30 so funnel
 * analysis can split by placement and by exact copy without re-deriving the
 * page from `cta_location`.
 */
export function GuideStartCta({
  slug,
  headline,
  body,
  position = "intro",
}: {
  /** Guide slug, so a signup can be traced to the article that produced it. */
  slug: string;
  /** Overrides the registry headline (rare; the registry is the default). */
  headline?: string;
  /** Overrides the registry body (rare; the registry is the default). */
  body?: string;
  /** Reported as `cta_position`; the intro card keeps the historical default. */
  position?: GuideCtaPosition;
}) {
  const topic = getGuideCtaCopy(slug);
  const resolvedHeadline = headline ?? topic.headline;
  const resolvedBody = body ?? topic.body;

  return (
    <div
      data-guide-cta={position}
      className="border-t border-gray-100 bg-white px-4 py-10 sm:px-6"
    >
      <div className="mx-auto max-w-3xl rounded-xl border border-accent/30 bg-accent-light p-6 sm:p-8">
        <p className="font-heading text-xl font-extrabold tracking-tight text-navy sm:text-2xl">
          {resolvedHeadline}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-muted sm:text-base">
          {resolvedBody}
        </p>
        {topic.intent === "business-walkthrough" ? (
          <>
            {/* Business-intent guide (conversion programme, 31 Oct 2026):
                the walkthrough is the ask; the free account stays as a small
                secondary link. Both emit guide_cta_clicked with their own
                destination, so the split is measurable. */}
            <GuideCtaButton
              slug={slug}
              position={position}
              ctaCopy={resolvedHeadline}
              label={`${BUSINESS_WALKTHROUGH_CTA} →`}
              href={BUSINESS_WALKTHROUGH_HREF}
            />
            <p className="mt-3 text-sm text-muted">
              Just need the paperwork for one job?{" "}
              <GuideCtaButton
                slug={slug}
                position={position}
                ctaCopy={resolvedHeadline}
                label="Start free"
                className="font-semibold text-brand-700 underline underline-offset-4 hover:text-accent-dark"
              />
            </p>
          </>
        ) : (
          <GuideCtaButton
            slug={slug}
            position={position}
            ctaCopy={resolvedHeadline}
            label="Start free →"
          />
        )}
      </div>
    </div>
  );
}

/**
 * The tracked button shared by every guide CTA placement. Exported for the
 * post-tool and post-example prompts and for the instrumented end-of-article
 * footer blocks (the pre-existing per-page footer CTA — its own copy and
 * styling, now emitting the same event instead of sitting untracked above a
 * second card).
 */
export function GuideCtaButton({
  slug,
  position,
  ctaCopy,
  label,
  href,
  className,
}: {
  slug: string;
  position: GuideCtaPosition;
  /** The CTA copy reported on the event. Defaults to the visible label. */
  ctaCopy?: string;
  label: string;
  href?: string;
  className?: string;
}) {
  const destination = href ?? guideSignupHref(slug);
  const reportedCopy = ctaCopy ?? label;
  return (
    <AttributedLink
      href={destination}
      data-guide-cta-position={position}
      className={
        className ??
        "mt-5 inline-flex min-h-[48px] items-center justify-center rounded-[10px] bg-accent px-7 py-3 text-[15px] font-bold text-white transition-colors hover:bg-accent-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-dark"
      }
      onClick={() => {
        if (!posthog.__loaded) return;
        posthog.capture("guide_cta_clicked", {
          cta_location: `guide:${slug}`,
          destination,
          entry_path:
            typeof window === "undefined" ? null : window.location.pathname,
          guide_slug: slug,
          cta_position: position,
          cta_copy: reportedCopy,
        });
      }}
    >
      {label}
    </AttributedLink>
  );
}
